import React, { useState, useRef, useEffect } from 'react';
import { PortfolioItem } from '@/hooks/useProfile';
import { usePortfolioModalState } from './usePortfolioModalState';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ImageOff, ZoomIn, X, ZoomOut } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface MobileShotsTabProps {
  item: PortfolioItem;
}

const MobileShotsTab = ({ item }: MobileShotsTabProps) => {
  const {
    images,
    imageError,
    handleImageError
  } = usePortfolioModalState(item);

  // Zoom state
  const [isZoomed, setIsZoomed] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0, distance: 0 });
  const lastTapRef = useRef<number>(0);
  const doubleTapTimeoutRef = useRef<NodeJS.Timeout>();
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleZoomClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsZoomed(true);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClose = () => {
    setIsZoomed(false);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.5, 1));
  };

  const handleDoubleTap = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    if (scale === 1) {
      setScale(2);
      const image = imageRef.current;
      if (image) {
        const scaledWidth = image.width * 2;
        const scaledHeight = image.height * 2;
        const maxX = (scaledWidth - container.clientWidth) / 2;
        const maxY = (scaledHeight - container.clientHeight) / 2;
        
        const offsetX = (x - container.clientWidth / 2) * 2;
        const offsetY = (y - container.clientHeight / 2) * 2;
        
        setPosition({
          x: Math.max(-maxX, Math.min(maxX, offsetX)),
          y: Math.max(-maxY, Math.min(maxY, offsetY))
        });
      }
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const getDistance = (touches: TouchList | React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setTouchStart({ x: touch.clientX, y: touch.clientY, distance: 0 });
      
      // Handle double tap
      const now = Date.now();
      const timeDiff = now - lastTapRef.current;
      if (timeDiff < 300 && timeDiff > 0) {
        handleDoubleTap(touch.clientX, touch.clientY);
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    } else if (e.touches.length === 2) {
      setTouchStart({ x: 0, y: 0, distance: getDistance(e.touches) });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStart.x;
      const deltaY = touch.clientY - dragStart.y;
      
      setPosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setDragStart({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      const newDistance = getDistance(e.touches);
      const delta = newDistance - touchStart.distance;
      
      if (Math.abs(delta) > 10) {
        const zoomFactor = delta > 0 ? 1.1 : 0.9;
        setScale(prev => Math.max(1, Math.min(3, prev * zoomFactor)));
        setTouchStart(prev => ({ ...prev, distance: newDistance }));
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      
      setPosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(1, Math.min(3, prev * delta)));
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (doubleTapTimeoutRef.current) {
        clearTimeout(doubleTapTimeoutRef.current);
      }
    };
  }, []);

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <ImageOff className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Images</h3>
        <p className="text-gray-500">This portfolio item doesn't have any images yet.</p>
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="h-full">
        <div className="space-y-4 px-0">
          {images.map((image, index) => (
            <div key={image.id || index} className="space-y-2">
              {/* Image */}
              <div className="overflow-hidden bg-gray-100 rounded-none">
                {imageError[image.id || index] ? (
                  <div className="flex items-center justify-center h-64 bg-gray-200">
                    <ImageOff className="w-8 h-8 text-gray-400" />
                  </div>
                ) : (
                  <div
                    className="relative w-full h-full group cursor-zoom-in"
                    onClick={() => handleZoomClick(index)}
                    role="button"
                    tabIndex={0}
                    aria-label="Zoom image"
                  >
                    <img
                      src={image.url}
                      alt={`${item.title} - Image ${index + 1}`}
                      className="w-full h-auto object-contain transition-all duration-300 group-hover:scale-[1.01]"
                      onError={() => handleImageError(image.id || index.toString(), image.url)}
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-black/30 rounded-full p-2">
                        <ZoomIn className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Image Caption */}
              {image.caption && (
                <p className="text-sm text-gray-600 px-2">
                  {image.caption}
                </p>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Zoom Modal */}
      <Dialog open={isZoomed} onOpenChange={handleClose}>
        <DialogContent className="max-w-full sm:max-w-[95vw] max-h-screen sm:max-h-[95vh] p-0 bg-black/90 border-none flex flex-col overflow-y-auto justify-center items-center">
          <div
            ref={containerRef}
            className="relative w-full flex-1 flex items-center justify-center overflow-auto min-h-[300px] sm:min-h-[300px] px-0 py-0"
            style={{ minHeight: '300px', minWidth: 0 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {images[selectedImageIndex] && (
              <>
                <img
                  ref={imageRef}
                  src={images[selectedImageIndex].url}
                  alt={`${item.title} - zoomed view`}
                  className="object-contain transition-transform duration-200 select-none w-full h-auto max-h-[70vh] sm:max-h-[90vh] sm:max-w-[95vw] mx-auto shadow-lg bg-white"
                  style={{
                    transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                    cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    touchAction: 'none'
                  }}
                  draggable={false}
                />
                
                {/* Zoom Controls */}
                <div className="absolute left-1/2 -translate-x-1/2 bottom-2 sm:bottom-6 flex items-center justify-center gap-2 bg-black/70 rounded-xl px-3 py-2 shadow-md z-30 max-w-full sm:max-w-[320px] w-auto">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 h-8 w-8"
                    onClick={handleZoomOut}
                    disabled={scale <= 1}
                  >
                    <ZoomOut className="h-5 w-5" />
                  </Button>
                  <span className="text-white text-base min-w-[2.5rem] text-center select-none">
                    {Math.round(scale * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 h-8 w-8"
                    onClick={handleZoomIn}
                    disabled={scale >= 3}
                  >
                    <ZoomIn className="h-5 w-5" />
                  </Button>
                </div>

                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="fixed sm:absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/70 hover:bg-black/90 text-white rounded-full p-2 transition-colors z-40"
                >
                  <X className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MobileShotsTab; 